import {isEmpty} from "lodash";

import {
    applyAddNodes,
    applyAddPolygon,
    applyRemoveNodes,
    applySetNodeLabel,
    applySetNodeLabelMulti,
    applySetNodeRulesMulti,
    applySetNodeUserData,
    applySetRuleSetRules,
    applySetRuleSetSelector,
} from "../actions/pcbNodeActions";
import {
    applyDuplicateGlobalPcbRuleSet,
    applyRemoveGlobalPcbRuleSet,
    applyRemoveNodeRules,
    applySetGlobalPcbRuleSet,
    applySetNodeRules,
    applyToggleGlobalPcbRule,
    applyToggleNodeSpecificRule,
} from "../actions/pcbRulesActions";
import {performDocumentMutation} from "../documentMutations";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type PcbSliceState = Pick<
    DocumentStoreState,
    | "setGlobalPcbRuleSet"
    | "setNodeRules"
    | "removeNodeRules"
    | "toggleGlobalPcbRule"
    | "toggleNodeSpecificRule"
    | "duplicateGlobalPcbRuleSet"
    | "removeGlobalPcbRuleSet"
    | "addNodes"
    | "addPolygon"
    | "removeNodes"
    | "setNodeLabel"
    | "setNodeLabelMulti"
    | "setNodeUserData"
    | "setNodeRulesMulti"
    | "setRuleSetSelector"
    | "setRuleSetRules"
>;

export function createPcbSlice(deps: SliceDependencies): PcbSliceState {
    const {primitives, get} = deps;

    return {
        setGlobalPcbRuleSet: (setPcbLayoutRules, selectedGlobalPcbLayoutRule) => {
            if (!setPcbLayoutRules || isEmpty(setPcbLayoutRules)) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => applySetGlobalPcbRuleSet(draft, setPcbLayoutRules, selectedGlobalPcbLayoutRule),
                {
                    actionType: "setGlobalPcbRuleSet",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                    additionalPayload: {
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                    },
                },
            );
        },

        setNodeRules: (updatedNodes) => {
            if (!updatedNodes) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applySetNodeRules(draft, updatedNodes), {
                actionType: "setNodeRules",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                },
            });
        },

        removeNodeRules: (nodeId, ruleKeys) => {
            if (!ruleKeys || !nodeId) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyRemoveNodeRules(draft, nodeId, ruleKeys), {
                actionType: "removeNodeRules",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                },
            });
        },

        toggleGlobalPcbRule: (ruleId, state, subRuleId) => {
            if (!ruleId) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyToggleGlobalPcbRule(draft, ruleId, state, subRuleId), {
                actionType: "toggleGlobalPcbRule",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                },
            });
        },

        toggleNodeSpecificRule: (ruleId, nodeId, toggleState) => {
            if (!ruleId || !nodeId) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => applyToggleNodeSpecificRule(draft, ruleId, nodeId, toggleState),
                {
                    actionType: "toggleNodeSpecificRule",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                    additionalPayload: {
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                    },
                },
            );
        },

        duplicateGlobalPcbRuleSet: (ruleUids) => {
            if (!ruleUids || ruleUids.length === 0) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyDuplicateGlobalPcbRuleSet(draft, ruleUids), {
                actionType: "duplicateGlobalPcbRuleSet",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                },
            });
        },

        removeGlobalPcbRuleSet: (pcbLayoutRuleUids) => {
            if (!pcbLayoutRuleUids || pcbLayoutRuleUids.length === 0) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyRemoveGlobalPcbRuleSet(draft, pcbLayoutRuleUids), {
                actionType: "removeGlobalPcbRuleSet",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                },
            });
        },

        addNodes: (nodes, inPcbEditor) => {
            const document = get().document;
            if (!document) return;
            performDocumentMutation(primitives, (draft) => applyAddNodes(draft, nodes, document), {
                actionType: "addNodes",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {inPcbEditor},
            });
        },

        addPolygon: (node, updatedNodePositions, inPcbEditor) => {
            performDocumentMutation(primitives, (draft) => applyAddPolygon(draft, node, updatedNodePositions), {
                actionType: "addPolygon",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {inPcbEditor},
            });
        },

        removeNodes: (removeNodeUids, toggleBakedNodes = [], updatedNodesRulesSetsMap = {}) => {
            performDocumentMutation(
                primitives,
                (draft) => applyRemoveNodes(draft, removeNodeUids, toggleBakedNodes, updatedNodesRulesSetsMap),
                {
                    actionType: "removeNodes",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        setNodeLabel: (nodeUid, label, canUndo = true) => {
            performDocumentMutation(primitives, (draft) => applySetNodeLabel(draft, nodeUid, label), {
                actionType: "setNodeLabel",
                canUndo,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                },
            });
        },

        setNodeLabelMulti: (nodeUidLabelMap) => {
            performDocumentMutation(primitives, (draft) => applySetNodeLabelMulti(draft, nodeUidLabelMap), {
                actionType: "setNodeLabelMulti",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },

        setNodeUserData: (nodeUid, userData) => {
            performDocumentMutation(primitives, (draft) => applySetNodeUserData(draft, nodeUid, userData), {
                actionType: "setNodeUserData",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },

        setNodeRulesMulti: (nodeUidRulesMap) => {
            performDocumentMutation(primitives, (draft) => applySetNodeRulesMulti(draft, nodeUidRulesMap), {
                actionType: "setNodeRulesMulti",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                },
            });
        },

        setRuleSetSelector: (ruleSetUid, selector) => {
            performDocumentMutation(primitives, (draft) => applySetRuleSetSelector(draft, ruleSetUid, selector), {
                actionType: "setRuleSetSelector",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },

        setRuleSetRules: (ruleSetUid, rules) => {
            performDocumentMutation(primitives, (draft) => applySetRuleSetRules(draft, ruleSetUid, rules), {
                actionType: "setRuleSetRules",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },
    };
}
